// Phase 6.2 — superadmin impersonation ("view as <user>").
//
// Holds the active impersonation target (or null = "All users")
// and keeps the API client's impersonation header in sync. The
// context lives ABOVE routing (inside AuthProvider) so the scope
// persists as the operator navigates /browse ↔ /status, and so
// the ScopeSelector on either page reads the same state.
//
// State is intentionally NOT persisted to localStorage — it resets
// to "All users" on every page reload. An admin should never be
// silently impersonating across browser sessions; a fresh load is
// always the unimpersonated, all-users view.
//
// The provider ALSO loads the user directory (list + per-user
// workspace counts) the ScopeSelector dropdown needs. That load
// happens ONCE, while NOT impersonating — which matters: GET /users
// is superadmin-only and the backend downgrades an impersonated
// request to role=user, so /users would 403 mid-impersonation.
// Loading here (app-level, before any impersonation, and surviving
// route remounts) sidesteps that entirely. Counts come from
// /system/status's owned_by_username (Phase 6.1 CHANGE 5), grouped
// client-side — no extra backend endpoint.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  getSystemStatus,
  listManagedUsers,
  setImpersonationUserId,
  type ManagedUser,
} from "../lib/api";
import { useAuth } from "./AuthContext";

/** The active impersonation target. Mirrors what the backend needs
 *  (userId) plus what the UI shows (username, email). */
export interface ImpersonationTarget {
  userId: string;
  username: string;
  email: string | null;
}

/** One dropdown row: a user plus their owned-workspace count. */
export interface DirectoryEntry {
  user: ManagedUser;
  workspaceCount: number;
}

export interface ImpersonationState {
  /** Active target, or null when viewing as "All users". */
  current: ImpersonationTarget | null;
  /** Set (or clear with null) the impersonation target. Only the
   *  superadmin UI ever calls this with a non-null value; the
   *  backend rejects a non-superadmin who sends the header anyway. */
  setImpersonation: (target: ImpersonationTarget | null) => void;
  /** Convenience: setImpersonation(null). */
  clearImpersonation: () => void;
  /** Stable string identity of the current scope ("all" or the
   *  target's userId). Used as a remount key so scoped pages
   *  refetch when the scope changes. */
  scopeKey: string;
  /** Directory for the ScopeSelector dropdown: every user + their
   *  owned-workspace count. Loaded once for superadmin. Empty for
   *  regular users (who never see the selector). */
  directory: DirectoryEntry[];
  /** True while the directory's one-time load is in flight. */
  directoryLoading: boolean;
}

const Ctx = createContext<ImpersonationState | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { status, role } = useAuth();
  const [current, setCurrent] = useState<ImpersonationTarget | null>(null);
  const [directory, setDirectory] = useState<DirectoryEntry[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryLoaded, setDirectoryLoaded] = useState(false);

  const setImpersonation = useCallback(
    (target: ImpersonationTarget | null) => {
      // Sync the API client FIRST (synchronously) so the refetch
      // the scope change triggers already carries the header.
      setImpersonationUserId(target?.userId ?? null);
      setCurrent(target);
    },
    [],
  );

  const clearImpersonation = useCallback(() => {
    setImpersonation(null);
  }, [setImpersonation]);

  // Clear impersonation if the session ends (logout within the SPA).
  // Without this a stale header could ride along after re-login.
  useEffect(() => {
    if (status !== "authenticated") {
      setImpersonationUserId(null);
      setCurrent(null);
    }
  }, [status]);

  // One-time directory load for the dropdown. Gated on:
  //   * authenticated superadmin (regular users never see the selector
  //     and GET /users would 403 for them)
  //   * not currently impersonating (so /users + /system/status are
  //     UNSCOPED — full list + all owners for the counts)
  //   * not already loaded
  useEffect(() => {
    if (
      status !== "authenticated" ||
      role !== "superadmin" ||
      directoryLoaded ||
      current !== null
    ) {
      return;
    }
    const ctrl = new AbortController();
    setDirectoryLoading(true);
    Promise.all([
      listManagedUsers(ctrl.signal),
      getSystemStatus(ctrl.signal),
    ])
      .then(([usersResp, statusResp]) => {
        if (ctrl.signal.aborted) return;
        // Count owned workspaces per username (owned_by_username is
        // the only owner key /system/status exposes).
        const countByUsername = new Map<string, number>();
        for (const w of statusResp.workspaces) {
          const owner = w.owned_by_username;
          if (owner) {
            countByUsername.set(owner, (countByUsername.get(owner) ?? 0) + 1);
          }
        }
        const entries: DirectoryEntry[] = usersResp.users
          .map((user) => ({
            user,
            workspaceCount: countByUsername.get(user.username) ?? 0,
          }))
          .sort((a, b) => a.user.username.localeCompare(b.user.username));
        setDirectory(entries);
        setDirectoryLoaded(true);
      })
      .catch(() => {
        // Non-fatal: the dropdown still offers "All users". Mark
        // loaded so we don't hammer a failing endpoint each render.
        if (ctrl.signal.aborted) return;
        setDirectoryLoaded(true);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setDirectoryLoading(false);
      });
    return () => ctrl.abort();
  }, [status, role, directoryLoaded, current]);

  const value = useMemo<ImpersonationState>(
    () => ({
      current,
      setImpersonation,
      clearImpersonation,
      scopeKey: current?.userId ?? "all",
      directory,
      directoryLoading,
    }),
    [current, setImpersonation, clearImpersonation, directory, directoryLoading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useImpersonation(): ImpersonationState {
  const ctx = useContext(Ctx);
  if (ctx === undefined) {
    throw new Error("useImpersonation must be used inside an ImpersonationProvider");
  }
  return ctx;
}
